import { useState, useRef, useEffect, useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../supabase'
import Topbar from '../components/Topbar'

// ── BD seasonal intelligence ──────────────────────────────────
function getBDSeasonalContext() {
  const now   = new Date()
  const month = now.getMonth() + 1
  const day   = now.getDate()
  const season = month >= 11 || month <= 2 ? 'Winter' :
                 month >= 3  && month <= 5  ? 'Spring/Summer' :
                 month >= 6  && month <= 8  ? 'Monsoon' : 'Autumn'
  const upcoming = []
  const addIfSoon = (name, m, d) => {
    const eventDate = new Date(now.getFullYear(), m - 1, d)
    if (eventDate < now) eventDate.setFullYear(now.getFullYear() + 1)
    const daysAway = Math.ceil((eventDate - now) / (1000 * 60 * 60 * 24))
    if (daysAway <= 30 && daysAway > 0) upcoming.push({ name, daysAway })
  }
  addIfSoon('Pohela Boishakh (Bengali New Year)', 4, 14)
  addIfSoon('Pohela Boshonto (Spring Festival)', 2, 13)
  addIfSoon('Christmas', 12, 25)
  addIfSoon('New Year', 1, 1)
  if (month >= 11 || month <= 2) upcoming.push({ name: 'Wedding Season', daysAway: 0 })
  if (month === 9 && day >= 15) upcoming.push({ name: 'Durga Puja', daysAway: 15 })
  if (month === 10 && day <= 15) upcoming.push({ name: 'Durga Puja', daysAway: 0 })
  return { season, upcoming, today: now.toLocaleDateString('en-BD', { weekday:'long', year:'numeric', month:'long', day:'numeric' }) }
}

const HASHTAG_BANK = {
  'Sarees':           ['#SareeLovers','#BangladeshiSaree','#SareeCollections','#DesiSaree','#সালোয়ার'],
  'Clothing':         ['#BDFashion','#FashionBD','#OnlineShopping','#FashionBangladesh'],
  'Jewellery':        ['#JewelleryBD','#GoldJewellery','#BridalJewellery','#গহনা'],
  'Beauty & Skincare':['#BeautyBD','#SkincareRoutine','#GlowUp','#BDBeauty'],
  'Food & Catering':  ['#HomemadeFood','#FoodBD','#HealthyEating','#খাবার'],
  'default':          ['#BDShopping','#OnlineShopping','#FCommerceBD','#PageConnect','#NewArrival','#LimitedStock'],
}
function getHashtags(category, shopName) {
  const catTags = HASHTAG_BANK[category] || HASHTAG_BANK['default']
  const shopTag = '#' + (shopName || '').replace(/\s+/g, '')
  return [shopTag, ...catTags, '#BDShopping', '#FCommerceBD', '#NewArrival'].slice(0, 12).join('\n')
}

const MANUAL_FORMAT = `[Hook line — grab attention, mention your shop name]

✨ **[Product Name]** ✨
🏷️ Code: [Product Code]

• [Benefit 1 — what the customer gains]
• [Benefit 2 — quality, comfort, style]
• [Benefit 3 — occasion, use case]

💌 Inbox us for price
⚡ Limited stock available!

📦 Inbox [Shop Name] to order

#ShopName #Category #NewArrival #BDFashion`

// ── Lightbox ──────────────────────────────────────────────────
function Lightbox({ canvasRef, onClose }) {
  const ref = useRef()
  useEffect(() => {
    const src = canvasRef.current
    const dst = ref.current
    if (!src || !dst) return
    const maxW = Math.min(window.innerWidth - 40, 600)
    const maxH = window.innerHeight - 100
    const ratio = src.height / src.width
    let w = maxW, h = Math.round(maxW * ratio)
    if (h > maxH) { h = maxH; w = Math.round(maxH / ratio) }
    dst.width  = src.width
    dst.height = src.height
    dst.style.width  = w + 'px'
    dst.style.height = h + 'px'
    dst.style.borderRadius = '12px'
    dst.getContext('2d').drawImage(src, 0, 0)
  }, [canvasRef])

  return (
    <div onClick={onClose}
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.92)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', zIndex:300, padding:'20px' }}>
      <button onClick={onClose}
        style={{ position:'absolute', top:'16px', right:'16px', width:'38px', height:'38px', borderRadius:'50%', background:'rgba(255,255,255,0.15)', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><path d="M6 18L18 6M6 6l12 12"/></svg>
      </button>
      <canvas ref={ref} onClick={e=>e.stopPropagation()} style={{ display:'block', boxShadow:'0 20px 60px rgba(0,0,0,0.5)' }}/>
      <div style={{ marginTop:'16px', fontSize:'12px', color:'rgba(255,255,255,0.4)' }}>Click anywhere to close</div>
    </div>
  )
}

export default function ContentStudio() {
  const { seller, getSubscriptionPlan } = useAuth()
  const plan  = getSubscriptionPlan?.() || 'trial'
  const hasAI = ['growth','pro','business'].includes(plan)

  // ── Product selection ──
  const [products, setProducts]         = useState([])
  const [search, setSearch]             = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedProduct, setSelected]  = useState(null)
  const [loadingProducts, setLoadingProducts] = useState(true)
  const [lastPosted, setLastPosted]     = useState(null)
  const searchRef = useRef()

  // ── Mode ──
  const [mode, setMode] = useState(hasAI ? 'ai' : 'manual')

  // ── AI settings ──
  const [postLanguage, setPostLanguage] = useState('english')
  const [postStyle,    setPostStyle]    = useState('casual')
  const [ctaType,      setCtaType]      = useState('inbox')
  const [priceDisplay, setPriceDisplay] = useState('tara')

  // ── Caption ──
  const [caption,    setCaption]    = useState('')
  const [generating, setGenerating] = useState(false)
  const [genError,   setGenError]   = useState('')
  const [copied,     setCopied]     = useState(false)
  const [priceRec,   setPriceRec]   = useState(null)

  // ── Post photo ──
  const [postPhoto,     setPostPhoto]     = useState(null)
  const [showCode,      setShowCode]      = useState(true)
  const [showWatermark, setShowWatermark] = useState(false)
  const [showLogo,      setShowLogo]      = useState(true)
  const [productCode,   setProductCode]   = useState('')
  const [downloading,   setDownloading]   = useState(false)
  const [lightbox,      setLightbox]      = useState(false)
  const [shopLogo,      setShopLogo]      = useState('')
  const [logoLoaded,    setLogoLoaded]    = useState(false)
  const logoImgRef  = useRef()
  const photoInputRef = useRef()
  const canvasRef     = useRef()

  const sym = seller?.seller_currency === 'USD' ? '$' : seller?.seller_currency === 'AUD' ? 'A$' : '৳'
  const shopName = seller?.shop_name || ''

  // ── Canvas layout constants (% of output width so always proportional) ──
  const CANVAS_W = 1080
  const HEADER   = Math.round(CANVAS_W * 0.05)  // 54px at 1080
  const FOOTER   = Math.round(CANVAS_W * 0.04)  // 43px at 1080
  const TEAL_PX  = 2

  useEffect(() => {
    fetchProducts()
    if (seller?.logo_url) setShopLogo(seller.logo_url)
  }, []) // eslint-disable-line

  const fetchProducts = async () => {
    setLoadingProducts(true)
    const { data } = await supabase.from('products')
      .select('id,name,product_code,category,sell_price,image_url,post_photo_url,color,season,stock,additional_details,offer_active,offer_type,offer_value,offer_label,offer_valid_until')
      .eq('seller_id', seller?.id).order('name')
    setProducts(data || [])
    setLoadingProducts(false)
  }

  const fetchLastPosted = async (productId) => {
    const { data } = await supabase.from('fb_posts')
      .select('posted_at').eq('product_id', productId)
      .order('posted_at', { ascending: false }).limit(1)
    if (data?.[0]?.posted_at) {
      const d     = new Date(data[0].posted_at)
      const days  = Math.floor((Date.now() - d) / 86400000)
      setLastPosted(days === 0 ? 'Posted today' : days === 1 ? 'Posted yesterday' : `Last posted ${days} days ago`)
    } else {
      setLastPosted('Never posted')
    }
  }

  const handleSelectProduct = (p) => {
    setSelected(p)
    setSearch(p.name)
    setShowDropdown(false)
    setProductCode(p.product_code || '')
    setCaption('')
    setPriceRec(null)
    setGenError('')
    setPostPhoto(p.post_photo_url || p.image_url || null)
    fetchLastPosted(p.id)
  }

  const filteredProducts = products.filter(p =>
    search === '' || p.name.toLowerCase().includes(search.toLowerCase()) || (p.product_code || '').toLowerCase().includes(search.toLowerCase())
  )

  const isActiveOffer = (p) => p.offer_active && !(p.offer_valid_until && new Date(p.offer_valid_until) < new Date())

  // ── Canvas draw ──────────────────────────────────────────────
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || !postPhoto) return
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const W      = 1080
      const imgH   = Math.round(W * img.naturalHeight / img.naturalWidth)
      const H      = HEADER + TEAL_PX + imgH + TEAL_PX + FOOTER
      canvas.width  = W
      canvas.height = H
      const ctx   = canvas.getContext('2d')
      const NAVY  = '#0B1F3A'
      const TEAL  = '#2DD4B0'

      // White bg
      ctx.fillStyle = '#fff'
      ctx.fillRect(0, 0, W, H)

      // ── Header ──
      ctx.fillStyle = NAVY
      ctx.fillRect(0, 0, W, HEADER)

      // Logo
      let logoEndX = 14
      if (showLogo && shopLogo && logoLoaded && logoImgRef.current) {
        const logo   = logoImgRef.current
        const logoH  = HEADER - 8
        const logoW  = logoH * (logo.naturalWidth / logo.naturalHeight)
        ctx.drawImage(logo, 10, (HEADER - logoH) / 2, logoW, logoH)
        logoEndX = 10 + logoW + 8
      }
      // Shop name
      if (shopName) {
        ctx.fillStyle    = '#fff'
        ctx.font         = `${Math.round(HEADER * 0.38)}px Arial`
        ctx.textAlign    = 'left'
        ctx.textBaseline = 'middle'
        ctx.fillText(shopName, logoEndX, HEADER / 2)
      }

      // ── Teal line top ──
      ctx.fillStyle = TEAL
      ctx.fillRect(0, HEADER, W, TEAL_PX)

      // ── Image ──
      const imgAreaY = HEADER + TEAL_PX
      ctx.save()
      ctx.beginPath()
      ctx.rect(0, imgAreaY, W, imgH)
      ctx.clip()
      ctx.fillStyle = '#f8f8f8'
      ctx.fillRect(0, imgAreaY, W, imgH)
      // scale to fill width
      const scale = W / img.naturalWidth
      ctx.drawImage(img, 0, imgAreaY, W, img.naturalHeight * scale)

      // Watermark
      if (showWatermark && shopName) {
        ctx.globalAlpha  = 0.15
        ctx.fillStyle    = '#fff'
        ctx.font         = `bold ${Math.round(W * 0.05)}px Arial`
        ctx.textAlign    = 'center'
        ctx.textBaseline = 'middle'
        ctx.translate(W / 2, imgAreaY + imgH / 2)
        ctx.rotate(-Math.PI / 6)
        const step = W * 0.4
        for (let dy = -H; dy < H; dy += step) ctx.fillText('© ' + shopName, 0, dy)
      }
      ctx.restore()

      // ── Teal line bottom ──
      ctx.fillStyle = TEAL
      ctx.fillRect(0, imgAreaY + imgH, W, TEAL_PX)

      // ── Footer ──
      const footerY = imgAreaY + imgH + TEAL_PX
      ctx.fillStyle = NAVY
      ctx.fillRect(0, footerY, W, FOOTER)
      // Code left
      if (showCode && productCode) {
        ctx.fillStyle    = '#fff'
        ctx.font         = `bold ${Math.round(FOOTER * 0.38)}px "Courier New", monospace`
        ctx.textAlign    = 'left'
        ctx.textBaseline = 'middle'
        ctx.fillText(productCode, 14, footerY + FOOTER / 2)
      }
      // PageConnect right
      ctx.globalAlpha  = 0.6
      ctx.fillStyle    = TEAL
      ctx.font         = `${Math.round(FOOTER * 0.28)}px Arial`
      ctx.textAlign    = 'right'
      ctx.textBaseline = 'middle'
      ctx.fillText('Powered by pageconnect.io', W - 14, footerY + FOOTER / 2)
      ctx.globalAlpha  = 1
    }
    img.src = postPhoto
  }, [postPhoto, showCode, showWatermark, showLogo, productCode, shopName, shopLogo, logoLoaded, HEADER, FOOTER, TEAL_PX]) // eslint-disable-line

  useEffect(() => { drawCanvas() }, [drawCanvas])

  // ── Generate caption ─────────────────────────────────────────
  const handleGenerate = async () => {
    if (!selectedProduct) return
    setGenerating(true); setGenError('')
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('fb-post-product', {
        body: {
          product_name:       selectedProduct.name,
          product_code:       productCode || selectedProduct.product_code,
          category:           selectedProduct.category,
          sell_price:         selectedProduct.sell_price,
          additional_details: selectedProduct.additional_details,
          color:              selectedProduct.color,
          season:             selectedProduct.season,
          stock:              selectedProduct.stock,
          shop_name:          shopName,
          seller_currency:    seller?.seller_currency || 'BDT',
          post_language:      postLanguage,
          post_style:         postStyle,
          cta_type:           ctaType,
          whatsapp_number:    seller?.phone,
          price_display:      priceDisplay,
          preview_only:       true,
          seasonal_context:   JSON.stringify(getBDSeasonalContext()),
        }
      })
      if (fnErr || !data?.success) throw new Error(fnErr?.message || data?.error || 'Generation failed')
      setCaption(data.caption)
      if (data.price_recommendation) setPriceRec(data.price_recommendation)
    } catch (err) {
      setGenError(err.message || 'Failed to generate. Try again.')
    }
    setGenerating(false)
  }

  const handleManualHashtags = () => {
    if (!selectedProduct) return
    const tags = getHashtags(selectedProduct.category, shopName)
    setCaption(prev => prev + '\n\n' + tags)
  }

  const handleCopy = () => {
    if (!caption) return
    navigator.clipboard.writeText(caption)
    setCopied(true); setTimeout(() => setCopied(false), 2000)
  }

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setPostPhoto(URL.createObjectURL(file))
    e.target.value = ''
  }

  const handleDownloadPhoto = async () => {
    if (!canvasRef.current) return
    setDownloading(true)
    try {
      canvasRef.current.toBlob(async (blob) => {
        if (!blob) { setDownloading(false); return }
        const url  = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href     = url
        link.download = `${selectedProduct?.product_code || 'post'}-fb.jpg`
        link.click()
        URL.revokeObjectURL(url)
        if (selectedProduct && seller) {
          const path = `${seller.id}/${selectedProduct.id}-post.jpg`
          const { error } = await supabase.storage.from('product-images').upload(path, blob, { upsert:true, contentType:'image/jpeg' })
          if (!error) {
            const { data: urlData } = supabase.storage.from('product-images').getPublicUrl(path)
            if (urlData?.publicUrl) await supabase.from('products').update({ post_photo_url: urlData.publicUrl }).eq('id', selectedProduct.id)
          }
        }
        setDownloading(false)
      }, 'image/jpeg', 0.92)
    } catch { setDownloading(false) }
  }

  const handleOpenFacebook = () => window.open(seller?.facebook_url || 'https://facebook.com', '_blank')

  const Pill = ({ value, current, onChange, children }) => (
    <button onClick={() => onChange(value)}
      style={{ padding:'6px 14px', borderRadius:'20px', border:'1px solid', borderColor:current===value?'var(--primary)':'var(--border)', background:current===value?'var(--primary)':'var(--bg-card)', color:current===value?'#fff':'var(--text-muted)', fontSize:'12px', fontWeight:600, cursor:'pointer', fontFamily:'inherit', transition:'all 0.12s', whiteSpace:'nowrap' }}>
      {children}
    </button>
  )

  const Toggle = ({ value, onChange, label, sub }) => (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:'var(--bg-muted)', borderRadius:'10px' }}>
      <div>
        <div style={{ fontSize:'12.5px', fontWeight:600, color:'var(--text-primary)' }}>{label}</div>
        {sub && <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'2px' }}>{sub}</div>}
      </div>
      <button onClick={() => onChange(!value)}
        style={{ width:'38px', height:'20px', borderRadius:'10px', background:value?'var(--success)':'var(--border)', border:'none', cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
        <div style={{ position:'absolute', top:'2px', left:value?'20px':'2px', width:'16px', height:'16px', borderRadius:'50%', background:'#fff', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }}/>
      </button>
    </div>
  )

  return (
    <div style={{ flex:1, background:'var(--bg-page)', overflowX:'hidden', fontFamily:'Space Grotesk, sans-serif' }}>
      <Topbar title="Content Studio"/>

      {/* Hidden logo img for canvas */}
      {shopLogo && <img ref={logoImgRef} src={shopLogo} alt="" crossOrigin="anonymous" onLoad={()=>setLogoLoaded(true)} style={{ display:'none' }}/>}

      <style>{`
        @keyframes as-spin { to { transform: rotate(360deg) } }
        @keyframes as-fade { from { opacity:0; transform:translateY(6px) } to { opacity:1; transform:none } }
        .as-fade { animation: as-fade 0.3s ease-out both }
        .as-card { background: var(--bg-card); border: 1px solid var(--border); border-radius: 14px; padding: 18px; margin-bottom: 16px }
        .cs-dd-item:hover { background: var(--bg-muted) }
        @media(max-width:768px) { .as-layout { flex-direction: column !important } }
      `}</style>

      <div style={{ padding:'20px 24px', paddingBottom:'80px', maxWidth:'1100px', margin:'0 auto' }}>

        <div style={{ marginBottom:'20px' }}>
          <div style={{ fontSize:'22px', fontWeight:800, color:'var(--text-primary)', letterSpacing:'-0.4px', marginBottom:'4px' }}>Content Studio</div>
          <div style={{ fontSize:'13px', color:'var(--text-muted)' }}>Create captions and post photos for your Facebook page</div>
        </div>

        <div className="as-layout" style={{ display:'flex', gap:'20px', alignItems:'flex-start' }}>

          {/* ── LEFT ── */}
          <div style={{ flex:'0 0 460px', minWidth:0 }}>

            {/* Product search */}
            <div className="as-card">
              <div style={{ fontSize:'12px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:'10px' }}>Select product</div>
              <div style={{ position:'relative' }} ref={searchRef}>
                <input
                  value={search}
                  onChange={e => { setSearch(e.target.value); setShowDropdown(true); if (!e.target.value) { setSelected(null); setLastPosted(null) } }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder={loadingProducts ? 'Loading products...' : 'Search by name or code...'}
                  style={{ width:'100%', padding:'10px 36px 10px 14px', border:'1px solid var(--border)', borderRadius:'10px', fontSize:'13px', fontFamily:'inherit', color:'var(--text-primary)', background:'var(--bg-card)', outline:'none', boxSizing:'border-box' }}
                  onBlur={() => setTimeout(() => setShowDropdown(false), 150)}/>
                <div style={{ position:'absolute', right:'12px', top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                </div>

                {/* Dropdown */}
                {showDropdown && filteredProducts.length > 0 && (
                  <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, zIndex:50, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'10px', boxShadow:'var(--shadow-lg)', maxHeight:'260px', overflowY:'auto' }}>
                    {filteredProducts.map(p => (
                      <div key={p.id} className="cs-dd-item"
                        onMouseDown={() => handleSelectProduct(p)}
                        style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 14px', cursor:'pointer', borderBottom:'1px solid var(--border)' }}>
                        {p.image_url
                          ? <img src={p.image_url} alt={p.name} style={{ width:'36px', height:'36px', borderRadius:'6px', objectFit:'cover', flexShrink:0 }}/>
                          : <div style={{ width:'36px', height:'36px', borderRadius:'6px', background:'var(--bg-muted)', flexShrink:0 }}/>
                        }
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:'13px', fontWeight:600, color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.name}</div>
                          <div style={{ fontSize:'11px', color:'var(--text-muted)' }}>{p.product_code} · Stock: {p.stock}</div>
                        </div>
                        {isActiveOffer(p) && <span style={{ fontSize:'10px', fontWeight:700, padding:'2px 7px', borderRadius:'20px', background:'rgba(45,212,176,0.12)', color:'var(--success)', flexShrink:0 }}>OFFER</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected product card */}
              {selectedProduct && (
                <div style={{ marginTop:'12px' }}>
                  <div style={{ display:'flex', gap:'10px', padding:'10px 12px', background:'var(--bg-muted)', borderRadius:'10px', marginBottom:'8px' }}>
                    {selectedProduct.image_url
                      ? <img src={selectedProduct.image_url} alt={selectedProduct.name} style={{ width:'44px', height:'44px', borderRadius:'8px', objectFit:'cover', flexShrink:0 }}/>
                      : <div style={{ width:'44px', height:'44px', borderRadius:'8px', background:'var(--border)', flexShrink:0 }}/>
                    }
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:'13px', fontWeight:700, color:'var(--text-primary)' }}>{selectedProduct.name}</div>
                      <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginTop:'2px' }}>
                        {selectedProduct.product_code} · {selectedProduct.category} · Stock: {selectedProduct.stock}
                      </div>
                      {selectedProduct.sell_price && <div style={{ fontSize:'12px', color:'var(--success)', fontWeight:600, marginTop:'2px' }}>{sym}{Number(selectedProduct.sell_price).toLocaleString()}</div>}
                    </div>
                  </div>

                  {/* Offer details */}
                  {isActiveOffer(selectedProduct) && (
                    <div style={{ padding:'8px 12px', background:'rgba(45,212,176,0.08)', border:'1px solid rgba(45,212,176,0.2)', borderRadius:'8px', marginBottom:'8px', display:'flex', alignItems:'center', gap:'8px' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2" strokeLinecap="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                      <div style={{ fontSize:'12px', color:'var(--success)', fontWeight:600 }}>
                        {selectedProduct.offer_type === 'percent' ? selectedProduct.offer_value + '% off' :
                         selectedProduct.offer_type === 'fixed'   ? sym + selectedProduct.offer_value + ' off' :
                         selectedProduct.offer_label || 'Active offer'}
                        {selectedProduct.offer_valid_until && ` · until ${new Date(selectedProduct.offer_valid_until).toLocaleDateString('en-BD',{day:'numeric',month:'short'})}`}
                      </div>
                    </div>
                  )}

                  {/* Last posted hint */}
                  {lastPosted && (
                    <div style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'11.5px', color: lastPosted === 'Never posted' ? 'var(--text-muted)' : lastPosted.includes('today') || lastPosted.includes('yesterday') ? 'var(--warning)' : 'var(--text-muted)' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      {lastPosted}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Mode toggle */}
            <div className="as-card" style={{ padding:'6px' }}>
              <div style={{ display:'flex', gap:'4px' }}>
                {hasAI && (
                  <button onClick={() => setMode('ai')}
                    style={{ flex:1, padding:'10px', borderRadius:'10px', border:'none', background:mode==='ai'?'var(--primary)':'transparent', color:mode==='ai'?'#fff':'var(--text-muted)', fontSize:'13px', fontWeight:600, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 2C12 2 12.8 7.5 14.5 9.5C16.2 11.5 22 12 22 12C22 12 16.2 12.5 14.5 14.5C12.8 16.5 12 22 12 22"/></svg>
                    AI Generate
                  </button>
                )}
                <button onClick={() => setMode('manual')}
                  style={{ flex:1, padding:'10px', borderRadius:'10px', border:'none', background:mode==='manual'?'var(--primary)':'transparent', color:mode==='manual'?'#fff':'var(--text-muted)', fontSize:'13px', fontWeight:600, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  Write manually
                </button>
              </div>
            </div>

            {/* AI settings */}
            {mode === 'ai' && hasAI && (
              <div className="as-card as-fade">
                <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
                  <div>
                    <div style={{ fontSize:'12px', fontWeight:600, color:'var(--text-muted)', marginBottom:'8px' }}>Language</div>
                    <div style={{ display:'flex', gap:'7px', flexWrap:'wrap' }}>
                      <Pill value="english" current={postLanguage} onChange={setPostLanguage}>English</Pill>
                      <Pill value="bengali" current={postLanguage} onChange={setPostLanguage}>বাংলা</Pill>
                      <Pill value="mixed"   current={postLanguage} onChange={setPostLanguage}>Mixed</Pill>
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize:'12px', fontWeight:600, color:'var(--text-muted)', marginBottom:'8px' }}>Style</div>
                    <div style={{ display:'flex', gap:'7px', flexWrap:'wrap' }}>
                      <Pill value="casual"       current={postStyle} onChange={setPostStyle}>Casual</Pill>
                      <Pill value="professional" current={postStyle} onChange={setPostStyle}>Professional</Pill>
                      <Pill value="festive"      current={postStyle} onChange={setPostStyle}>Festive</Pill>
                    </div>
                    <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'5px' }}>Festive only when a major event is within 30 days</div>
                  </div>
                  <div>
                    <div style={{ fontSize:'12px', fontWeight:600, color:'var(--text-muted)', marginBottom:'8px' }}>How customers order</div>
                    <div style={{ display:'flex', gap:'7px', flexWrap:'wrap' }}>
                      <Pill value="inbox"   current={ctaType} onChange={setCtaType}>Inbox</Pill>
                      <Pill value="comment" current={ctaType} onChange={setCtaType}>Comment BOOK</Pill>
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize:'12px', fontWeight:600, color:'var(--text-muted)', marginBottom:'8px' }}>Price in post</div>
                    <div style={{ display:'flex', gap:'7px', flexWrap:'wrap' }}>
                      <Pill value="hide" current={priceDisplay} onChange={setPriceDisplay}>Hide price</Pill>
                      <Pill value="show" current={priceDisplay} onChange={setPriceDisplay}>Show price</Pill>
                      <Pill value="tara" current={priceDisplay} onChange={setPriceDisplay}>Ask Tara</Pill>
                    </div>
                  </div>
                  {genError && <div style={{ padding:'10px', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'8px', fontSize:'12.5px', color:'#DC2626' }}>{genError}</div>}
                  <button onClick={handleGenerate} disabled={generating || !selectedProduct}
                    style={{ width:'100%', padding:'12px', borderRadius:'12px', background:generating||!selectedProduct?'var(--border)':'var(--primary)', border:'none', fontSize:'13.5px', fontWeight:700, color:generating||!selectedProduct?'var(--text-muted)':'#fff', cursor:generating||!selectedProduct?'not-allowed':'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
                    {generating && <div style={{ width:'15px', height:'15px', borderRadius:'50%', border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', animation:'as-spin 0.7s linear infinite' }}/>}
                    {generating ? 'Tara is writing...' : !selectedProduct ? 'Select a product first' : 'Generate caption'}
                  </button>
                </div>
              </div>
            )}

            {/* Tara price rec */}
            {priceRec && priceDisplay === 'tara' && (
              <div className="as-card as-fade" style={{ borderColor:'rgba(45,212,176,0.3)', background:'rgba(45,212,176,0.04)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px' }}>
                  <div style={{ width:'20px', height:'20px', borderRadius:'50%', background:'var(--primary)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none"><path d="M12 2C12 2 12.8 7.5 14.5 9.5C16.2 11.5 22 12 22 12C22 12 16.2 12.5 14.5 14.5C12.8 16.5 12 22 12 22C12 22 11.2 16.5 9.5 14.5C7.8 12.5 2 12 2 12C2 12 7.8 11.5 9.5 9.5C11.2 7.5 12 2 12 2Z" fill="#2DD4B0"/></svg>
                  </div>
                  <div style={{ fontSize:'12.5px', fontWeight:700, color:'var(--primary)' }}>Tara recommends: {priceRec.recommendation === 'hide' ? 'Hide price' : 'Show price'}</div>
                </div>
                <div style={{ fontSize:'12px', color:'var(--text-muted)', lineHeight:1.6 }}>{priceRec.reason}</div>
              </div>
            )}

            {/* Caption editor */}
            <div className="as-card">
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'10px' }}>
                <div style={{ fontSize:'12px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.07em' }}>Caption</div>
                <div style={{ display:'flex', gap:'6px' }}>
                  {mode === 'manual' && (
                    <button onClick={handleManualHashtags} disabled={!selectedProduct}
                      style={{ fontSize:'11.5px', fontWeight:600, color:'var(--primary)', background:'var(--primary-light)', border:'none', borderRadius:'7px', padding:'4px 10px', cursor:selectedProduct?'pointer':'not-allowed', fontFamily:'inherit' }}>
                      + Hashtags
                    </button>
                  )}
                  <button onClick={handleCopy} disabled={!caption}
                    style={{ fontSize:'11.5px', fontWeight:600, color:copied?'var(--success)':'var(--text-muted)', background:'var(--bg-muted)', border:'1px solid var(--border)', borderRadius:'7px', padding:'4px 10px', cursor:caption?'pointer':'not-allowed', fontFamily:'inherit', display:'flex', alignItems:'center', gap:'4px' }}>
                    {copied ? '✓ Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
              {mode === 'manual' && !caption && (
                <div style={{ fontSize:'12px', color:'var(--text-muted)', background:'var(--bg-muted)', borderRadius:'8px', padding:'10px 12px', marginBottom:'10px', lineHeight:1.7, fontFamily:'monospace', whiteSpace:'pre-line' }}>
                  {MANUAL_FORMAT}
                </div>
              )}
              <textarea value={caption} onChange={e => setCaption(e.target.value)}
                placeholder={mode === 'manual' ? 'Write your caption following the format guide above...' : 'Click "Generate caption" to create with AI, or type here...'}
                rows={12}
                style={{ width:'100%', border:'1px solid var(--border)', borderRadius:'10px', padding:'12px 14px', fontSize:'13px', fontFamily:'inherit', color:'var(--text-primary)', background:'var(--bg-card)', resize:'vertical', outline:'none', lineHeight:1.7, boxSizing:'border-box' }}
                onFocus={e => e.target.style.borderColor = 'var(--success)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}/>
              <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'6px' }}>
                {caption.length} characters · {caption.split(/\s+/).filter(w => w.startsWith('#')).length} hashtags
              </div>
            </div>

            {/* Open Facebook */}
            <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'14px', padding:'16px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px' }}>
              <div>
                <div style={{ fontSize:'13px', fontWeight:700, color:'var(--text-primary)', marginBottom:'3px' }}>Ready to post?</div>
                <div style={{ fontSize:'12px', color:'var(--text-muted)' }}>Copy the caption, download the photo, then paste on Facebook.</div>
              </div>
              <button onClick={handleOpenFacebook}
                style={{ display:'flex', alignItems:'center', gap:'6px', padding:'10px 16px', borderRadius:'10px', background:'#1877F2', border:'none', fontSize:'13px', fontWeight:700, color:'#fff', cursor:'pointer', fontFamily:'inherit', flexShrink:0 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                Open Facebook
              </button>
            </div>
          </div>

          {/* ── RIGHT: Post photo ── */}
          <div style={{ flex:1, minWidth:0 }}>
            <div className="as-card">
              <div style={{ fontSize:'12px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:'14px' }}>Post photo</div>

              {/* Upload */}
              <input ref={photoInputRef} type="file" accept="image/*" style={{ display:'none' }} onChange={handlePhotoSelect}/>
              <button onClick={() => photoInputRef.current?.click()}
                style={{ width:'100%', padding:'10px', borderRadius:'10px', border:'1.5px dashed var(--border)', background:'var(--bg-muted)', fontSize:'13px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer', fontFamily:'inherit', marginBottom:'14px', display:'flex', alignItems:'center', justifyContent:'center', gap:'7px' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
                {postPhoto ? 'Change photo' : 'Upload post photo'}
              </button>

              {/* Overlays */}
              <div style={{ display:'flex', flexDirection:'column', gap:'8px', marginBottom:'14px' }}>
                <Toggle value={showLogo} onChange={setShowLogo}
                  label="Brand header"
                  sub={shopLogo ? 'Shop logo + name at top' : 'Shop name at top (no logo found)'}/>
                <div style={{ display:'flex', gap:'8px', alignItems:'center', padding:'10px 14px', background:'var(--bg-muted)', borderRadius:'10px' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:'12.5px', fontWeight:600, color:'var(--text-primary)' }}>Product code footer</div>
                    <input value={productCode} onChange={e => setProductCode(e.target.value)}
                      placeholder="e.g. SAR-RS-RED-761"
                      style={{ marginTop:'5px', width:'100%', padding:'6px 10px', border:'1px solid var(--border)', borderRadius:'7px', fontSize:'12px', fontFamily:'monospace', color:'var(--text-primary)', background:'var(--bg-card)', outline:'none', boxSizing:'border-box' }}/>
                  </div>
                  <button onClick={() => setShowCode(s => !s)}
                    style={{ width:'38px', height:'20px', borderRadius:'10px', background:showCode?'var(--success)':'var(--border)', border:'none', cursor:'pointer', position:'relative', flexShrink:0, transition:'background 0.2s', marginTop:'20px' }}>
                    <div style={{ position:'absolute', top:'2px', left:showCode?'20px':'2px', width:'16px', height:'16px', borderRadius:'50%', background:'#fff', transition:'left 0.2s' }}/>
                  </button>
                </div>
                <Toggle value={showWatermark} onChange={setShowWatermark}
                  label="Shop watermark"
                  sub={`Diagonal © ${shopName} overlay`}/>
              </div>

              {/* Canvas preview */}
              {postPhoto ? (
                <div style={{ position:'relative', marginBottom:'14px', cursor:'pointer' }} onClick={() => setLightbox(true)}>
                  <canvas ref={canvasRef} style={{ width:'100%', borderRadius:'10px', border:'1px solid var(--border)', display:'block' }}/>
                  <div style={{ position:'absolute', bottom:'10px', right:'10px', padding:'5px 10px', borderRadius:'7px', background:'rgba(11,31,58,0.7)', display:'flex', alignItems:'center', gap:'5px', backdropFilter:'blur(4px)' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"/></svg>
                    <span style={{ fontSize:'11px', color:'#fff', fontWeight:600 }}>Full preview</span>
                  </div>
                </div>
              ) : (
                <div style={{ aspectRatio:'4/5', background:'var(--bg-muted)', borderRadius:'10px', border:'1px dashed var(--border)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'14px' }}>
                  <div style={{ textAlign:'center' }}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.2" strokeLinecap="round" style={{ margin:'0 auto 8px', display:'block' }}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                    <div style={{ fontSize:'12px', color:'var(--text-muted)' }}>Upload a post photo above</div>
                    <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'3px' }}>Or select a product with an existing post photo</div>
                  </div>
                </div>
              )}

              {/* Download */}
              <button onClick={handleDownloadPhoto} disabled={!postPhoto || downloading}
                style={{ width:'100%', padding:'12px', borderRadius:'12px', background:!postPhoto||downloading?'var(--border)':'var(--primary)', border:'none', fontSize:'13.5px', fontWeight:700, color:!postPhoto||downloading?'var(--text-muted)':'#fff', cursor:!postPhoto||downloading?'not-allowed':'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
                {downloading && <div style={{ width:'15px', height:'15px', borderRadius:'50%', border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', animation:'as-spin 0.7s linear infinite' }}/>}
                {downloading ? 'Saving...' : 'Download & save photo'}
              </button>

              <div style={{ marginTop:'12px', padding:'10px 12px', background:'var(--bg-muted)', borderRadius:'8px', fontSize:'11.5px', color:'var(--text-muted)', lineHeight:1.6 }}>
                Photo saves to your product inventory as the post photo — reuse next time without re-uploading.
              </div>
            </div>

            {!hasAI && (
              <div style={{ background:'linear-gradient(135deg,var(--primary),#1a3a6e)', borderRadius:'14px', padding:'20px', color:'#fff' }}>
                <div style={{ fontSize:'14px', fontWeight:800, marginBottom:'6px' }}>Unlock AI caption generation</div>
                <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.7)', marginBottom:'14px', lineHeight:1.6 }}>Upgrade to Growth and let Tara write captions in seconds — English, Bengali or mixed.</div>
                <a href="/pricing" style={{ display:'inline-block', padding:'9px 18px', borderRadius:'9px', background:'#2DD4B0', color:'var(--primary)', fontSize:'13px', fontWeight:700, textDecoration:'none' }}>View plans →</a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && canvasRef.current && (
        <Lightbox canvasRef={canvasRef} onClose={() => setLightbox(false)}/>
      )}
    </div>
  )
}